import type { Layout, UISchemaElement } from '@jsonforms/core'

type AnyData = Record<string, unknown>

export const scopeToPath = (scope: string): string =>
	scope.replace(/^#\//, '').replaceAll('properties/', '').replaceAll('/', '.')

export const getAtPath = (data: AnyData, path: string): unknown => {
	let current: unknown = data
	for (const key of path.split('.')) {
		if (
			current === null ||
			current === undefined ||
			typeof current !== 'object'
		) {
			return undefined
		}
		current = (current as AnyData)[key]
	}
	return current
}

const deleteAtPath = (data: AnyData, path: string): AnyData => {
	const parts = path.split('.')
	if (parts.length === 1) {
		if (!(parts[0] in data)) {
			return data
		}
		const key = parts[0]
		const result = Object.fromEntries(
			Object.entries(data).filter(([k]) => k !== key)
		)
		return result
	}
	const [head, ...rest] = parts
	const nested = data[head]
	if (nested === null || nested === undefined || typeof nested !== 'object') {
		return data
	}
	const cleaned = deleteAtPath(nested as AnyData, rest.join('.'))
	if (cleaned === nested) {
		return data
	}
	return { ...data, [head]: cleaned }
}

export const evalCondition = (
	condition: Record<string, unknown>,
	data: AnyData
): boolean => {
	// Compound condition (AND / OR)
	if ('conditions' in condition && Array.isArray(condition.conditions)) {
		const type = (condition.type as string) ?? 'AND'
		const subs = condition.conditions as Record<string, unknown>[]
		return type === 'AND'
			? subs.every(c => evalCondition(c, data))
			: subs.some(c => evalCondition(c, data))
	}
	const scope = condition.scope as string | undefined
	const schema = condition.schema as Record<string, unknown> | undefined
	if (!scope || !schema) {
		return true
	}
	const value = getAtPath(data, scopeToPath(scope))
	if (
		'not' in schema &&
		schema.not !== null &&
		typeof schema.not === 'object'
	) {
		return !evalCondition(
			{ ...condition, schema: schema.not as Record<string, unknown> },
			data
		)
	}
	if ('const' in schema) {
		return value === schema.const
	}
	if ('enum' in schema && Array.isArray(schema.enum)) {
		return (schema.enum as unknown[]).includes(value)
	}
	return true
}

export const evalRule = (
	rule: UISchemaElement['rule'] | undefined,
	data: AnyData
): boolean => {
	if (!rule || rule.effect !== 'SHOW') {
		return true
	}
	return evalCondition(rule.condition as Record<string, unknown>, data)
}

/**
 * Walk the uiSchema tree and collect the scopes of visible FieldArrayBlock elements.
 * Used by pageHasErrors to detect validation errors inside array blocks without
 * affecting cleanHiddenFields (which lets FieldArrayBlockRenderer manage its own data).
 */
export const collectArrayBlockScopes = (
	elements: UISchemaElement[],
	data: AnyData,
	parentVisible: boolean,
	out: Map<string, boolean>
): void => {
	for (const el of elements) {
		const elVisible = parentVisible && evalRule(el.rule, data)

		if (el.type === 'FieldArrayBlock') {
			const scope = (el as { scope?: string }).scope
			if (scope) {
				out.set(scope, (out.get(scope) ?? false) || elVisible)
			}
			continue
		}

		const children = (el as { elements?: UISchemaElement[] }).elements
		if (children?.length) {
			collectArrayBlockScopes(children, data, elVisible, out)
		}
	}
}

/**
 * Walk the uiSchema tree and record, for each Control scope, whether at least
 * one control referencing that scope is currently visible.
 *
 * A scope is visible if its own rule passes AND all ancestor rules pass.
 * FieldArrayBlock elements are skipped — they manage their own data clearing.
 */
export const collectVisibility = (
	elements: UISchemaElement[],
	data: AnyData,
	parentVisible: boolean,
	out: Map<string, boolean>
): void => {
	for (const el of elements) {
		const elVisible = parentVisible && evalRule(el.rule, data)

		if (el.type === 'Control') {
			const scope = (el as { scope?: string }).scope
			if (scope) {
				// Mark visible if ANY control for this scope is visible
				out.set(scope, (out.get(scope) ?? false) || elVisible)
			}
		}

		// FieldArrayBlock items manage their own clearing via FieldArrayBlockRenderer
		if (el.type === 'FieldArrayBlock') {
			continue
		}

		const children = (el as { elements?: UISchemaElement[] }).elements
		if (children?.length) {
			collectVisibility(children, data, elVisible, out)
		}
	}
}

/**
 * Recursively remove keys whose value is an empty plain object `{}`.
 * Returns the same reference if nothing was pruned.
 */
const pruneEmptyObjects = (data: AnyData): AnyData => {
	let result = data
	for (const key of Object.keys(data)) {
		const val = result[key]
		if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
			const pruned = pruneEmptyObjects(val as AnyData)
			if (Object.keys(pruned).length === 0) {
				const k = key
				result = Object.fromEntries(
					Object.entries(result).filter(([ek]) => ek !== k)
				)
			} else if (pruned !== val) {
				result = { ...result, [key]: pruned }
			}
		}
	}
	return result
}

/**
 * Remove data for every Control scope that is not visible in the current form
 * state, then prune any empty objects left behind.
 * Returns the same object reference if nothing was removed.
 */
export const cleanHiddenFields = (data: AnyData, uiSchema: Layout): AnyData => {
	const visibility = new Map<string, boolean>()
	collectVisibility(uiSchema.elements ?? [], data, true, visibility)

	let result = data
	for (const [scope, isVisible] of visibility) {
		if (!isVisible) {
			const path = scopeToPath(scope)
			if (getAtPath(result, path) !== undefined) {
				result = deleteAtPath(result, path)
			}
		}
	}

	if (result !== data) {
		result = pruneEmptyObjects(result)
	}

	return result
}

/**
 * Return the dot-paths of every key that existed in `before` but is absent
 * (undefined) in `after`. Used to sync React Hook Form state after cleaning.
 */
export const findClearedPaths = (
	before: AnyData,
	after: AnyData,
	prefix = ''
): string[] => {
	const paths: string[] = []
	for (const key of Object.keys(before)) {
		const fullPath = prefix ? `${prefix}.${key}` : key
		const bVal = before[key]
		const aVal = (after as AnyData)[key]
		if (aVal === undefined && bVal !== undefined) {
			paths.push(fullPath)
		} else if (
			bVal !== null &&
			typeof bVal === 'object' &&
			!Array.isArray(bVal) &&
			aVal !== null &&
			typeof aVal === 'object' &&
			!Array.isArray(aVal)
		) {
			paths.push(
				...findClearedPaths(bVal as AnyData, aVal as AnyData, fullPath)
			)
		}
	}
	return paths
}
